import { observer } from 'mobx-react-lite'
import React, { useMemo, useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { Layout } from '../components/Layout'
import type { CellProps } from '../components/table2/ad_grid_columns'
import { type AgGridColumnDef, AgGridTable, DummyHeader } from '../components/table2/AgGridTable'
import { TableStore } from '../components/table2/table_store'
import { Button } from '../components/ui/button'
import { Menu, MenuItem } from '../components/ui/menu'
import { Filters, TableName } from '../config'
import { EllipsisMenu } from '../icons/icons'
import {
    CustomFieldCreateModal,
    CustomFieldDeleteModal,
    CustomFieldEditModal,
    ENTITY_TYPE_LABELS,
    fetchAllCustomFieldDefinitions,
    type CustomFieldDefinition,
    type CustomFieldEntityType,
} from '../features/custom-fields'
import { useSavedFiltersStore } from '../hooks/useSavedFiltersStore'
import { useUser } from '../hooks/useUser'
import type {
    LoadFunc,
    Params,
    PaginatedResponse,
} from '../packages/blueprint-components/src/useTableHooks'
import { useTableHooksFromStore } from '../packages/blueprint-components/src/useTableHooks'
import { userStore } from '../stores/user_store'

type CustomFieldRow = CustomFieldDefinition & { __actions?: string }

const compareRows = (a: CustomFieldRow, b: CustomFieldRow, field: string): number => {
    const av = String((a as any)[field] ?? '')
    const bv = String((b as any)[field] ?? '')
    return av.localeCompare(bv)
}

/**
 * The catalog endpoint isn't paginated (each entity type is capped at 50
 * definitions server-side, so the full set is always small), so this table's
 * "server" params — search, entity-type filter, sort, pagination — are all
 * applied here in JS rather than pushed down to a request.
 */
export const filterAndPaginateCustomFields = (
    definitions: CustomFieldRow[],
    params: Params,
): PaginatedResponse<CustomFieldRow>['data'] => {
    const entityTypeFilter = params.filter?.entity_type as CustomFieldEntityType | undefined
    const dataTypeFilter = params.filter?.data_type
    const query = String(params.query ?? '')
        .trim()
        .toLowerCase()

    let rows = entityTypeFilter
        ? definitions.filter((definition) => definition.entity_type === entityTypeFilter)
        : definitions

    if (dataTypeFilter !== undefined && dataTypeFilter !== null && dataTypeFilter !== '') {
        rows = rows.filter((definition) => Number(definition.data_type) === Number(dataTypeFilter))
    }

    if (query) {
        rows = rows.filter(
            (definition) =>
                definition.name.toLowerCase().includes(query) ||
                definition.key.toLowerCase().includes(query) ||
                (definition.description ?? '').toLowerCase().includes(query),
        )
    }

    const sort = params.sort as string | undefined
    if (sort) {
        const field = sort.startsWith('-') ? sort.slice(1) : sort
        const direction = sort.startsWith('-') ? -1 : 1
        rows = [...rows].sort((a, b) => direction * compareRows(a, b, field))
    }

    const perPage = params.perPage ?? 15
    const page = params.page ?? 1
    const total = rows.length
    const last_page = Math.max(1, Math.ceil(total / perPage))
    const from = total === 0 ? 0 : (page - 1) * perPage + 1
    const to = Math.min(page * perPage, total)

    return {
        data: rows.slice((page - 1) * perPage, page * perPage),
        current_page: page,
        last_page,
        from,
        to,
        total,
    }
}

interface CustomFieldsContext {
    onEdit: (definition: CustomFieldDefinition) => void
    onDelete: (definition: CustomFieldDefinition) => void
}

const EntityTypeCell = ({ data }: CellProps<CustomFieldRow>) => (
    <span>{ENTITY_TYPE_LABELS[data.entity_type]}</span>
)

const KeyCell = ({ data }: CellProps<CustomFieldRow>) => (
    <Link
        aria-label={`View values for ${data.name}`}
        className="text-blue-700 underline"
        to={`/custom-fields/${data.entity_type}/${data.definition_id}`}
    >
        <code className="whitespace-nowrap text-xs">{data.key}</code>
    </Link>
)

const TypeCell = ({ data }: CellProps<CustomFieldRow>) => (
    <span className="capitalize">{data.type}</span>
)

const ActionsCell = ({ data, context }: CellProps<CustomFieldRow>) => {
    const { onEdit, onDelete } = context as unknown as CustomFieldsContext
    const navigate = useNavigate()
    const menu = useMemo(
        () => (
            <Menu>
                <MenuItem
                    text="View values"
                    onClick={() =>
                        navigate(`/custom-fields/${data.entity_type}/${data.definition_id}`)
                    }
                />
                <MenuItem text="Edit" onClick={() => onEdit(data)} />
                <MenuItem text="Delete" onClick={() => onDelete(data)} />
            </Menu>
        ),
        [data, navigate, onEdit, onDelete],
    )

    return <EllipsisMenu content={menu} />
}

const agGridComponent = (component: unknown) => component as React.ReactNode

export const CUSTOM_FIELD_COLUMNS: AgGridColumnDef<CustomFieldRow>[] = [
    { field: 'name', headerName: 'Name', headerComponent: agGridComponent(DummyHeader) },
    {
        field: 'entity_type',
        headerName: 'Entity type',
        headerComponent: agGridComponent(DummyHeader),
        cellRenderer: agGridComponent(EntityTypeCell),
    },
    {
        field: 'key',
        headerName: 'Key',
        headerComponent: agGridComponent(DummyHeader),
        cellRenderer: agGridComponent(KeyCell),
    },
    {
        field: 'type',
        headerName: 'Type',
        headerComponent: agGridComponent(DummyHeader),
        cellRenderer: agGridComponent(TypeCell),
    },
    {
        field: 'description',
        headerName: 'Description',
        headerComponent: agGridComponent(DummyHeader),
    },
    { field: 'version', headerName: 'Version', headerComponent: agGridComponent(DummyHeader) },
    {
        field: '__actions',
        headerName: 'Actions',
        cellRenderer: agGridComponent(ActionsCell),
    },
]

export const CustomFields = observer(() => {
    const { activeProfile } = useUser()
    const profileId = activeProfile ? String(activeProfile.profile_id) : null
    const [createModalOpen, setCreateModalOpen] = useState(false)
    const [editingDefinition, setEditingDefinition] = useState<CustomFieldDefinition | null>(null)
    const [deletingDefinition, setDeletingDefinition] = useState<CustomFieldDefinition | null>(null)

    const [store] = useState<TableStore<CustomFieldRow>>(
        () => new TableStore(userStore, TableName.CustomFields),
    )
    const savedFiltersStore = useSavedFiltersStore()

    const load: LoadFunc<CustomFieldRow> = async (params) => {
        if (!profileId) return { data: filterAndPaginateCustomFields([], params) }
        const definitions = await fetchAllCustomFieldDefinitions(profileId)
        return { data: filterAndPaginateCustomFields(definitions, params) }
    }

    const tableHooks = useTableHooksFromStore(load, {}, false, store)

    const context = useMemo<CustomFieldsContext>(
        () => ({
            onEdit: setEditingDefinition,
            onDelete: setDeletingDefinition,
        }),
        [],
    )

    return (
        <Layout
            primary="Custom fields"
            breadcrumbs={[{ text: 'Custom fields' }]}
            rightActions={
                <Button onClick={() => setCreateModalOpen(true)}>Create custom field</Button>
            }
        >
            <div className="mx-auto max-w-6xl space-y-5 p-6">
                <div className="flex justify-end">
                    <Link className="text-sm text-blue-700 underline" to="/custom-fields/values">
                        Edit entity values
                    </Link>
                </div>

                <AgGridTable
                    name={TableName.CustomFields}
                    columns={CUSTOM_FIELD_COLUMNS}
                    tableHooks={tableHooks}
                    store={store}
                    savedFiltersStore={savedFiltersStore}
                    filters={[Filters.CustomFieldEntityType, Filters.CustomFieldDataType]}
                    search
                    searchPlaceholder="Search by name or key"
                    columnSelection
                    dateSelect={false}
                    context={context}
                />

                <CustomFieldCreateModal
                    isOpen={createModalOpen}
                    profileId={profileId}
                    onClose={() => setCreateModalOpen(false)}
                    onCreated={tableHooks.reload}
                />
                <CustomFieldEditModal
                    isOpen={!!editingDefinition}
                    definition={editingDefinition}
                    profileId={profileId ?? ''}
                    onClose={() => setEditingDefinition(null)}
                    onSaved={tableHooks.reload}
                />
                <CustomFieldDeleteModal
                    isOpen={!!deletingDefinition}
                    definition={deletingDefinition}
                    profileId={profileId ?? ''}
                    onClose={() => setDeletingDefinition(null)}
                    onDeleted={tableHooks.reload}
                />
            </div>
        </Layout>
    )
})
